// Contratos compartilhados entre o Worker (src/) e o app web (web/).
// Fonte: pesquisa/02-CLONE-SPEC.md §6 (dados), §7.3 (sync) e §8 (API).
// Regra: a UI escreve SÓ pela fila de saída (POST /v1/sync/push). REST de escrita é para extensão/API.

export type CardKind = "note" | "image" | "link" | "quote" | "highlight" | "color" | "pdf" | "video" | "audio" | "file";
export type IngestState = "uploading" | "queued" | "processing" | "ready" | "partial" | "failed";
export type Origin = "source" | "ai" | "user";

/** Documento ProseMirror/TipTap serializado. */
export type Doc = { type: "doc"; content?: unknown[] };

/** Card como o cliente enxerga (camelCase). `rev` = accounts.seq na última escrita. */
export interface Card {
  id: string;
  kind: CardKind;
  kindOrigin: Origin;
  entityType: string | null; // Schema.org: Article, Product, Book, Recipe...
  entity: Record<string, unknown> | null; // preço, autor, site_name...
  title: string | null;
  titleOrigin: Origin | null;
  sourceUrl: string | null;
  canonicalUrl: string | null;
  sourceDomain: string | null;
  content: Doc | null; // nota, citação, destaque
  contentText: string | null; // texto plano derivado (nota) ou corpo de leitura
  note: Doc | null; // anotação (1 por card)
  noteText: string | null;
  summary: string | null;
  ocrText: string | null;
  palette: Record<string, number> | null; // {"#rrggbb": peso 0..1}
  dominantColor: string | null;
  primaryBlobId: string | null;
  coverBlobId: string | null;
  width: number | null;
  height: number | null;
  ingestState: IngestState;
  ingestError: string | null; // fetch_403, fetch_timeout, too_large, unsupported...
  tagIds: string[];
  // ---- Fases 3–4 (paridade) ----
  isPrivate: boolean; // derivado da etiqueta reservada "privado"
  action: CardAction | null; // ler, assistir, ouvir, comprar, fazer (IA ou usuário)
  completedAt: number | null; // tarefa/ação concluída
  objects: string[]; // objetos reconhecidos na imagem (busca objeto:)
  language: string | null; // BCP 47 detectado (busca idioma:)
  keyPhrases: string[];
  readerAvailable: boolean; // há cópia de leitura (GET /v1/cards/:id/reader)
  spaceIds: string[]; // coleções manuais em que o card está (ordem em SpaceCard)
  lastReviewedAt: number | null; // Reencontro: "manter" grava agora
  createdAt: number;
  modifiedAt: number;
  bumpedAt: number;
  deletedAt: number | null;
  rev: number;
}

export type CardAction = "ler" | "assistir" | "ouvir" | "comprar" | "fazer";

export interface Tag {
  id: string;
  name: string;
  normalized: string; // minúsculas, sem acento
  origin: "user" | "ai" | "mixed"; // de onde vieram os vínculos (gerenciar etiquetas)
  rev: number;
}

/** Coleção. smart = consulta salva (gramática da busca), avaliada no cliente; exclusões em SpaceCard.excluded. */
export interface Space {
  id: string;
  name: string;
  normalized: string;
  color: string; // #rrggbb
  kind: "manual" | "smart";
  query: string | null;
  queryVersion: number | null;
  position: number;
  rev: number;
}

/** Pertença manual (ou exclusão manual de coleção automática). id = spaceId + ":" + cardId. */
export interface SpaceCard {
  spaceId: string;
  cardId: string;
  excluded: boolean;
  position: number | null;
  rev: number;
}

/** Em foco (máximo LIMITS.pins). id = cardId. */
export interface Pin {
  cardId: string;
  position: number;
  rev: number;
}

/** Ligação entre cards. wiki = derivada de [[ ]] no texto da nota (o servidor recalcula a cada escrita). */
export interface Link {
  id: string;
  sourceId: string;
  targetId: string;
  kind: "wiki" | "manual";
  rev: number;
}

/** Preferências sincronizadas (tema, densidade, legendas, fonte das notas...). id = accountId. */
export interface Prefs {
  values: Record<string, string | number | boolean>;
  rev: number;
}

// ---------- Pull: GET /v1/sync?since=<checkpoint>&limit=<1..500> ----------

export type SyncEntity = "card" | "tag" | "space" | "spaceCard" | "pin" | "link" | "prefs";

export type SyncItem =
  | { entity: "card"; id: string; rev: number; deleted: false; data: Card }
  | { entity: "tag"; id: string; rev: number; deleted: false; data: Tag }
  | { entity: "space"; id: string; rev: number; deleted: false; data: Space }
  | { entity: "spaceCard"; id: string; rev: number; deleted: false; data: SpaceCard }
  | { entity: "pin"; id: string; rev: number; deleted: false; data: Pin }
  | { entity: "link"; id: string; rev: number; deleted: false; data: Link }
  | { entity: "prefs"; id: string; rev: number; deleted: false; data: Prefs }
  | { entity: SyncEntity; id: string; rev: number; deleted: true };

/** Itens com rev > since, em ordem de rev. `more` = há mais páginas; repetir com since=checkpoint. */
export interface PullResponse {
  items: SyncItem[];
  checkpoint: number;
  more: boolean;
}

// ---------- Push: POST /v1/sync/push {ops} (≤ 50 por lote) ----------

/** Campos de card que o cliente pode alterar. Texto rico (content, note) exige baseRev (conflito). */
export interface CardFields {
  title: string | null;
  content: Doc | null;
  contentText: string | null;
  note: Doc | null;
  noteText: string | null;
  palette: Record<string, number> | null;
  dominantColor: string | null;
  width: number | null;
  height: number | null;
  kind: CardKind; // troca manual → servidor grava kindOrigin='user'
  entityType: string | null; // troca manual de tipo (Article, Recipe...) → kindOrigin='user'
  action: CardAction | null;
  completedAt: number | null;
  coverBlobId: string | null; // troca de capa (blob da própria conta)
  /** Origem da troca de kind/entityType: 'source' = derivado do texto (citação, cor); sem o campo, a troca é 'user'. */
  kindOrigin: Exclude<Origin, "ai">;
}

export type Op =
  | {
      opId: string; // ULID gerado no cliente = chave de idempotência
      type: "card.create";
      cardId: string; // ULID gerado no cliente
      data: {
        kind: CardKind;
        createdAt: number;
        title?: string | null;
        sourceUrl?: string; // kind=link → servidor normaliza, dedupe e enfileira ingestão
        content?: Doc | null;
        contentText?: string | null;
        note?: Doc | null; // anotação já no create (edição offline antes do primeiro push)
        noteText?: string | null;
        blobId?: string; // kind=image|pdf|video|audio|file → blob já enviado por PUT /v1/blobs/{sha256}
        palette?: Record<string, number> | null;
        dominantColor?: string | null;
        width?: number | null;
        height?: number | null;
        tags?: string[]; // nomes; criados se não existirem
      };
    }
  | { opId: string; type: "card.update"; cardId: string; baseRev: number; fields: Partial<CardFields> }
  | { opId: string; type: "card.delete"; cardId: string } // vai para a lixeira (deleted_at)
  | { opId: string; type: "card.restore"; cardId: string }
  | { opId: string; type: "tag.attach"; cardId: string; name: string }
  | { opId: string; type: "tag.detach"; cardId: string; tagId: string }
  // ---- Fase 4 ----
  | { opId: string; type: "card.review"; cardId: string } // Reencontro "manter": last_reviewed_at = agora
  | { opId: string; type: "tag.rename"; tagId: string; name: string }
  | { opId: string; type: "tag.merge"; tagId: string; into: string } // vínculos passam para "into"; tagId some
  | { opId: string; type: "tag.delete"; tagId: string } // desvincula de todos; cards ficam
  | { opId: string; type: "space.create"; spaceId: string; data: { name: string; color: string; kind: "manual" | "smart"; query?: string | null; position: number } }
  | { opId: string; type: "space.update"; spaceId: string; fields: Partial<{ name: string; color: string; query: string | null; position: number }> }
  | { opId: string; type: "space.delete"; spaceId: string } // cards ficam
  | { opId: string; type: "space.add"; spaceId: string; cardId: string; position?: number | null } // também desfaz exclusão
  | { opId: string; type: "space.remove"; spaceId: string; cardId: string } // manual: sai; smart: vira excluded
  | { opId: string; type: "pin.set"; cardId: string; position: number } // rejected "limit" acima de LIMITS.pins
  | { opId: string; type: "pin.unset"; cardId: string }
  | { opId: string; type: "prefs.update"; values: Record<string, string | number | boolean> };

export type OpStatus = "applied" | "duplicate" | "conflict" | "rejected";

/** "duplicate" repete o resultado original (rev, dedupedInto, conflictCopyId, error) gravado em applied_ops. */
export interface OpResult {
  opId: string;
  status: OpStatus; // duplicate = opId já aplicado antes (idempotência)
  /** rev do card depois desta op (applied/conflict). O cliente usa como baseRev exato da próxima edição. */
  rev?: number;
  error?: string; // rejected: not_found, invalid, too_large...
  /** card.create de link/blob já existente: o card local some e este passa a valer (subiu ao topo). */
  dedupedInto?: string;
  /** conflict em content/note: a versão do cliente virou este card novo ("cópia em conflito"). */
  conflictCopyId?: string;
}

export interface PushRequest {
  ops: Op[];
}

export interface PushResponse {
  results: OpResult[];
  checkpoint: number;
}

/** WebSocket GET /v1/sync/live: o servidor só manda avisos; o cliente faz pull ao receber. */
export interface LiveMessage {
  checkpoint: number;
}

// ---------- Blobs: PUT /v1/blobs/{sha256} (corpo = bytes, ≤ 100 MB) ----------

export interface BlobResponse {
  blobId: string;
  mime: string;
  bytes: number;
  existed: boolean; // mesmo sha256 já enviado por esta conta
}

/** Conta da réplica local que faz o pedido (sync e REST do app web). Sessão de outra conta → 401 account_mismatch. */
export const REPLICA_HEADER = "x-faig-account";

export const LIMITS = {
  pushOps: 50,
  pins: 8,
  shareLinkHours: 24,
  serendipityMin: 20, // mínimo de cards elegíveis para abrir uma sessão
  pairingCodeMinutes: 10,
  pullPage: 500,
  blobBytes: 100 * 1024 * 1024,
  trashDays: 30,
  htmlBytes: 5 * 1024 * 1024,
  fetchTimeoutMs: 10_000,
} as const;

/**
 * MIME aceitos (§7.11), detectados pelos bytes mágicos (shared/mime.ts) no servidor e no app. Imagem vira card "image";
 * PDF, vídeo e áudio viram "pdf", "video" e "audio" (kindOfMime).
 */
export const ACCEPTED_MIME = [
  "image/jpeg", "image/png", "image/gif", "image/webp", "image/avif", "image/heic", "image/heif", "image/svg+xml",
  "application/pdf", "video/mp4", "video/quicktime", "video/webm", "audio/mpeg", "audio/mp4", "audio/ogg", "audio/wav",
] as const;

// ---------- Fase 2: captura de fora do app ----------

/** POST /v1/devices/pair (sessão web) → código de 6 dígitos para a extensão ou o atalho iOS. */
export interface PairResponse {
  code: string;
  expiresAt: number;
}
/** POST /v1/devices/claim {code, label} → token Bearer (mostrado uma vez; o servidor guarda só o hash). */
export interface ClaimResponse {
  token: string;
  deviceId: string;
}
/** GET /v1/devices · DELETE /v1/devices/:id (revogar). */
export interface Device {
  id: string;
  label: string | null;
  kind: "device" | "api";
  scopes: string[];
  createdAt: number;
  lastSeenAt: number | null;
}
/** POST /v1/devices/keys (só sessão web) → chave de API, mostrada uma vez; o servidor guarda só o sha256 (= key.id). */
export interface ApiKeyResponse {
  token: string;
  key: Device;
}
/**
 * POST /v1/capture (Bearer ou cookie; multipart/form-data): url | text | file (imagem, PDF, vídeo, áudio) + title, tags
 * (vírgula), note, screenshot (imagem da aba) e html (só com url: o HTML da aba, até LIMITS.htmlBytes, lido no lugar de
 * buscar a página; página atrás de login guarda o conteúdo). Mesma semântica de card.create (dedupe incluído).
 */
export interface CaptureResponse {
  cardId: string;
  deduped: boolean;
}

// ---------- Fases 3–4: busca no servidor, parecidos, leitura, link temporário, uso ----------

/** GET /v1/search?q=&limit= (FTS5 + filtros; "~" no início = semântica Vectorize; híbrido por RRF). */
export interface ServerSearchResponse {
  ids: string[];
  mode: "lexical" | "semantic" | "hybrid";
  /** false: a consulta pedia semântica ("~") e Workers AI ou Vectorize falharam; `ids` vêm só do léxico (mode "lexical"). */
  semantic?: false;
}
/** GET /v1/cards/:id/similar?k= → imagens com estética parecida (Parecidos), só da própria conta. */
export interface SimilarResponse {
  ids: string[];
}
/** GET /v1/cards/:id/reader → Markdown sanitizado da cópia de leitura (text/markdown). POST /v1/cards/:id/reprocess → 202. */
/** POST /v1/cards/:id/share → link público de 24 h; DELETE /v1/share/:tokenId revoga; GET /s/:token = página pública noindex. */
export interface ShareResponse {
  url: string;
  tokenId: string;
  expiresAt: number;
}
/** GET /v1/me/usage → contadores da tela "Conta e uso". */
export interface UsageResponse {
  cards: number;
  bytes: number;
  aiCallsToday: number;
  aiDailyLimit: number;
  /** A análise automática (Claude) está ligada neste ambiente: false sem ANTHROPIC_API_KEY. */
  aiEnabled: boolean;
}
